import React, { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeSettings,
  closeMenu,
  toggleBackgroundMusic,
  toggleSoundEffects,
  setMasterVolume,
} from "../../../../State/actions";
import {
  selectSettingsActive,
  selectMasterVolume,
} from "../../../../State/selectors";
import { getNewUITexture } from "../../../../assets";
import { SOUND_NAME } from "../../../../sounds";
import { PixiSettings } from "../../../../pixiSettings";
import {
  USER_INTERFACE_BUTTON_CONFIG,
  FONT_CONFIG,
} from "../../../../pixiSettings";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import Checkbox from "../Buttons/Checkbox";
import VolumeBar from "../Buttons/VolumeBar";
import { soundManager } from "../../../../SoundManager";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import anime from "animejs";


// ── Overlay constants ─────────────────────────────────────────────────────
const OVERLAY_ALPHA = 0.7;
const OVERLAY_FADE_MS = 200;

// Padding/font config — identical to BetSelectPopup for consistent content area
const SETTINGS_CONFIG = {
  basePadding: {
    horizontal: 28,
    vertical: 40,
  },
  baseTitleFontSize: 42,
  baseTitleGap: 26,

  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 2.0, v: 7.0 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 10.0, v: 2.4 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 1.5,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.25,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
      titleFontMultiplier: 0.9,
      titleGapMultiplier: 0.75,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 0.5,
    },
  },
};

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settingsActive = useSelector(selectSettingsActive);
  const {
    dimensions,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
    isMobileSmall,
  } = useDimensions();
  const containerRef = useRef(null);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  // Get sound settings from Redux
  const { soundEffectsEnabled, backgroundMusicEnabled } = useSelector(
    (state) => state.sound
  );
  const masterVolume = useSelector(selectMasterVolume);

  // Memoize device/orientation checks
  const isDesktop = useMemo(
    () => device === PixiSettings.DEVICE.DESKTOP,
    [device]
  );




  // Memoize popup frame texture lookup
  const popupFrameTexture = useMemo(
    () => getNewUITexture("popup_settings"),
    []
  );

  // Determine if should show popup frame (not fullscreen) - memoized
  const showPopupFrame = useMemo(
    () =>
      isDesktop ||
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE),
    [isDesktop, device, orientation]
  );

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  // Sound toggle handlers - memoized
  const handleMusicToggle = useCallback(
    (value) => {
      soundManager.play(SOUND_NAME.button_click);
      dispatch(toggleBackgroundMusic());
    },
    [dispatch]
  );

  const handleSoundFXToggle = useCallback(
    (value) => {
      soundManager.play(SOUND_NAME.button_click);
      dispatch(toggleSoundEffects());
    },
    [dispatch]
  );

  // ── Volume slider ↔ actual volume mapping ──────────────────────────────────
  // Slider center (0.5) = volume 0.7 (client's default starting volume).
  // Left half:  slider 0→0.5   maps to volume 0→0.7
  // Right half: slider 0.5→1.0 maps to volume 0.7→1.0
  const sliderToVolume = useCallback((s) => {
    if (s <= 0.5) return s * 1.4;           // 0→0.5 → 0→0.7
    return 0.7 + (s - 0.5) * 0.6;           // 0.5→1.0 → 0.7→1.0
  }, []);

  const volumeToSlider = useCallback((v) => {
    if (v <= 0.7) return v / 1.4;            // 0→0.7 → 0→0.5
    return 0.5 + (v - 0.7) / 0.6;            // 0.7→1.0 → 0.5→1.0
  }, []);

  // Volume change handler - converts slider position to actual volume
  const handleVolumeChange = useCallback(
    (sliderValue) => {
      const volume = sliderToVolume(sliderValue);
      dispatch(setMasterVolume(volume));
      soundManager.setMasterVolume(volume);
    },
    [dispatch, sliderToVolume]
  );

  // ── Open animation (overlay fade → content fade) ─────────────────────────
  useEffect(() => {
    if (settingsActive && !isClosing) {
      // Fade in overlay
      const overlayAnim = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      // Fade in content
      let contentAnim = null;
      if (containerRef.current) {
        containerRef.current.alpha = 0;
        contentAnim = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }

      return () => {
        overlayAnim.pause();
        if (contentAnim) contentAnim.pause();
      };
    }
  }, [settingsActive, isClosing, showPopupFrame]);

  // ── Close animation (content fade → overlay fade → dispatch) ─────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    // Fade out content
    if (containerRef.current) {
      contentAnim = anime({
        targets: containerRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    // Fade out overlay (slight delay after content)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeSettings());
        dispatch(closeMenu());
        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch]);

  // Secondary toolbar — needed for popup width cap and positioning
  const toolbar = useSecondaryToolbar();

  // Calculate popup size: responsive based on viewport dimensions with max size cap
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) {
      return { width: 0, height: 0 };
    }

    // Get native texture dimensions
    const nativeWidth = popupFrameTexture.width;
    const nativeHeight = popupFrameTexture.height;


    if (showPopupFrame) {
      // Desktop & Tablet Landscape: Scale like AutoplayPopup
      const sizeMultiplier =
        device === PixiSettings.DEVICE.DESKTOP ? 0.8 : 1.25;
      const finalScale = uiSymbolScale * sizeMultiplier;
      return {
        width: nativeWidth * finalScale,
        height: nativeHeight * finalScale,
        scale: finalScale,
      };
    }

    // Mobile & Tablet Portrait: fullscreen — match BetSelectPopup width
    // Cap width to toolbar width so content stays within game area
    const maxWidth = toolbar?.width ? Math.min(dimensions.width, toolbar.width) : dimensions.width;
    return {
      width: maxWidth,
      height: dimensions.height,
    };
  }, [popupFrameTexture, showPopupFrame, device, uiSymbolScale, toolbar, dimensions]);

  // Calculate viewport configuration relative to parent container with device-specific padding
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    const config = SETTINGS_CONFIG[device]?.[orientation] || {
      paddingMultiplier: { h: 1.0, v: 1.0 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
    };

    if (showPopupFrame) {
      // Windowed mode (Tablet Landscape / Desktop)
      const popupScale = popupSize.width / popupFrameTexture.width;
      const paddingH =
        (SETTINGS_CONFIG.basePadding.horizontal *
          config.paddingMultiplier.h *
          popupScale) / 2;
      const paddingV =
        SETTINGS_CONFIG.basePadding.vertical *
        config.paddingMultiplier.v *
        popupScale;
      const contentWidth = popupSize.width - paddingH * 2;
      const contentHeight = popupSize.height - paddingV * 2;
      // Use same scale factor as AutoplayPopup for consistent title sizing
      const autoplayScaleFactor = uiSymbolScale * (device === PixiSettings.DEVICE.DESKTOP ? 0.8 : 1.25);
      const titleFontSize = Math.round(
        SETTINGS_CONFIG.baseTitleFontSize * config.titleFontMultiplier * autoplayScaleFactor / 2
      ) * 2 - 4;

      return {
        maxWidth: contentWidth,
        maxHeight: contentHeight,
        x: -popupSize.width / 2 + paddingH,
        y: -popupSize.height / 2 + paddingV,
        titleFontSize,
      };
    }

    // Fullscreen mode — identical to BetSelectPopup
    const paddingH =
      SETTINGS_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      uiSymbolScale;
    const paddingV =
      SETTINGS_CONFIG.basePadding.vertical *
      config.paddingMultiplier.v *
      uiSymbolScale;
    const contentWidth = popupSize.width - paddingH * 2;
    const contentHeight = dimensions.height - paddingV * 2;
    const titleFontSize = Math.round(
      SETTINGS_CONFIG.baseTitleFontSize *
      config.titleFontMultiplier *
      uiSymbolScale / 2
    ) * 2;

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -popupSize.width / 2 + paddingH,
      y: -dimensions.height / 2 + paddingV,
      titleFontSize,
    };
  }, [
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
    uiSymbolScale,
    device,
    orientation,
  ]);

  // Calculate responsive font sizes and layout multipliers
  const layoutConfig = useMemo(() => {
    const baseTitleSize = 42;
    const baseCheckboxLabelSize = 26;
    let titleMultiplier = 1.0;
    let checkboxLabelMultiplier = 1.0;
    let leftPaddingMultiplier = 1.0; // Multiplier for buttonConfig.width
    let titleToCheckboxSpacing = 1.3; // Gap from title to first checkbox (in itemSpacing units)
    let checkboxToCheckboxSpacing = 1.0; // Gap between checkboxes (in itemSpacing units)
    let sectionSpacing = 1.5; // Gap between sections (in itemSpacing units)

    if (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 2.5;
        checkboxLabelMultiplier = 2.2;
        leftPaddingMultiplier = 0.4;
        titleToCheckboxSpacing = 1.1;
        checkboxToCheckboxSpacing = 0.65;
        sectionSpacing = 0.75;
      } else {
        titleMultiplier = 2.25;
        checkboxLabelMultiplier = 2.05;
        leftPaddingMultiplier = 1.0;
        titleToCheckboxSpacing = 1.75;
        checkboxToCheckboxSpacing = 1.25;
        sectionSpacing = 1.5;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 3.0;
        checkboxLabelMultiplier = 2.5;
        leftPaddingMultiplier = 0.5;
        titleToCheckboxSpacing = 1.1;
        checkboxToCheckboxSpacing = 0.75;
        sectionSpacing = 1.0;
      } else {
        titleMultiplier = 1.7;
        checkboxLabelMultiplier = 1.7;
        leftPaddingMultiplier = 1.0;
        titleToCheckboxSpacing = 1.5;
        checkboxToCheckboxSpacing = 1.2;
        sectionSpacing = 1.3;
      }
    } else {
      // Desktop
      leftPaddingMultiplier = 1.0;
      titleToCheckboxSpacing = 1.3;
      checkboxToCheckboxSpacing = 1.1;
      sectionSpacing = 1.0;
    }

    return {
      titleFontSize: Math.round(
        baseTitleSize * titleMultiplier * uiSymbolScale
      ),
      checkboxLabelFontSize: Math.round(
        baseCheckboxLabelSize * checkboxLabelMultiplier * uiSymbolScale
      ),
      leftPaddingMultiplier,
      titleToCheckboxSpacing,
      checkboxToCheckboxSpacing,
      sectionSpacing,
    };
  }, [device, orientation, uiSymbolScale, isMobileSmall]);

  // Text style - main title (Settings)
  const dpr = window.devicePixelRatio || 1;
  const sectionStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: viewportConfig?.titleFontSize || 42,
        fontWeight: "600",
        fill: 0xffffff,
        align: "left",
        resolution: dpr,
      }),
    [viewportConfig?.titleFontSize, dpr]
  );

  // Text style - subtitles (Audio, Volume) — same size as checkbox labels, same weight as title
  const subtitleStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: layoutConfig.checkboxLabelFontSize,
        fontWeight: "600",
        fill: 0xffffff,
        align: "left",
        resolution: dpr,
      }),
    [layoutConfig.checkboxLabelFontSize, dpr]
  );

  // Get responsive button configuration
  const buttonConfig = useMemo(() => {
    return getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG);
  }, [getResponsiveConfig]);

  // Close button size multiplier — matches BetSelectPopup logic
  const closeButtonSizeMultiplier = useMemo(() => {
    if (showPopupFrame) return 0.35;
    if (device === PixiSettings.DEVICE.MOBILE && orientation === PixiSettings.ORIENTATION.LANDSCAPE) return 0.5;
    return 0.4;
  }, [showPopupFrame, device, orientation]);

  // Calculate vertical spacing for items
  // Total steps: 0.8 + 0.8 + 0.5 + 0.8 + 0.8 = 3.7 (last item at y=3.5)
  const itemSpacing = useMemo(() => {
    if (showPopupFrame && viewportConfig) {
      // Windowed: fill the content area evenly
      const availableHeight = viewportConfig.maxHeight - viewportConfig.titleFontSize;
      return availableHeight / 3.5;
    }
    return (viewportConfig?.titleFontSize || 42) * 2.0;
  }, [viewportConfig, showPopupFrame]);

  // Calculate left padding using buttonConfig.width with device-specific multiplier
  const leftPadding = useMemo(() => {
    return buttonConfig.width * layoutConfig.leftPaddingMultiplier;
  }, [buttonConfig.width, layoutConfig.leftPaddingMultiplier]);

  // Get checkbox size to align titles with checkbox left edge
  const checkboxSize = useMemo(() => {
    const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG);
    return buttonConfig.width * 0.7; // Using 0.7 multiplier (same as checkboxes)
  }, [getResponsiveConfig]);

  // Calculate title x position to align with checkbox left edge
  // Checkboxes are centered at leftPadding, so left edge is at leftPadding - checkboxSize/2
  // eslint-disable-next-line no-unused-vars
  const titleX = useMemo(() => {
    return leftPadding - checkboxSize / 2;
  }, [leftPadding, checkboxSize]);



  // Calculate Y positions using layoutConfig spacing values
  const yPositions = useMemo(() => {
    // Uniform visual gap between all items
    const step = 0.8;
    let y = 0;

    const settingsTitleY = y;
    y += step;
    const audioTitleY = y;
    y += step;
    const volumeBarY = y;
    y += step * 0.5; // Smaller step: bar has visual height, so less Y gap needed
    const volumeTitleY = y;
    y += step * 1.15; // Bigger gap between Volume title and checkboxes
    const musicY = y;
    y += step;
    const soundFXY = y;

    return {
      settingsTitleY,
      audioTitleY,
      volumeBarY,
      volumeTitleY,
      musicY,
      soundFXY,
      totalSteps: soundFXY, // used to scale itemSpacing
    };
  }, []);

  // Calculate close button position (top right of viewport) - absolute coordinates
  const closeButtonPosition = useMemo(() => {
    if (!viewportConfig || viewportConfig.maxWidth === 0) {
      return { x: 0, y: 0 };
    }

    const buttonSize = buttonConfig.width * closeButtonSizeMultiplier;
    const relativeX = viewportConfig.maxWidth - buttonSize / 2 - (showPopupFrame ? 6 : 0);
    // Windowed: vertically centered with title; Fullscreen: pinned to top-right corner
    const relativeY = showPopupFrame
      ? viewportConfig.titleFontSize / 2 - 4
      : buttonSize / 2;

    return {
      x: dimensions.width / 2 + viewportConfig.x + relativeX,
      y: dimensions.height / 2 + viewportConfig.y + relativeY,
    };
  }, [viewportConfig, buttonConfig, closeButtonSizeMultiplier, dimensions, showPopupFrame]);

  // Memoize Graphics draw functions
  const drawBackground = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height]
  );

  const drawPopupBackground = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0x161616, 1); // #161616 color
      g.drawRect(
        -dimensions.width / 2,
        -dimensions.height / 2,
        dimensions.width,
        dimensions.height
      );
      g.endFill();
    },
    [dimensions.width, dimensions.height]
  );

  // Popup container position — near settings button for windowed, centered for fullscreen
  const popupContainerPosition = useMemo(() => {
    if (showPopupFrame) {
      if (device === PixiSettings.DEVICE.DESKTOP) {
        // Desktop: exact mirror of AutoplayPopup but on the left side
        const toolbarX = (dimensions.width - toolbar.width) / 2;
        // AutoplayPopup: toolbarX + toolbar.width - buttonConfig.width * 4.4 (from right)
        // Mirror: toolbarX + buttonConfig.width * 4.4 (from left)
        const settingsRefX = toolbarX + buttonConfig.width;
        const toolbarTopY = dimensions.height - toolbar.height;
        const gap = toolbar.height * 0.25;
        return {
          x: settingsRefX + popupSize.width * 0.6,
          y: toolbarTopY - popupSize.height / 2 - gap,
        };
      }
      // Tablet landscape: anchor bottom edge above toolbar
      const toolbarTopY = dimensions.height - toolbar.height;
      const gap = toolbar.height * 0.25;
      return {
        x: popupSize.width * 0.5 + buttonConfig.width * 1.7,
        y: toolbarTopY - popupSize.height / 2 - gap,
      };
    }
    return {
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    };
  }, [
    showPopupFrame,
    dimensions,
    popupSize,
    buttonConfig,
    device,
    toolbar.height,
    toolbar.width,
  ]);

  // Memoize translation texts
  const settingsTitleText = useMemo(() => t("ui_settings_title"), [t]);
  const audioTitleText = useMemo(() => t("ui_settings_audio"), [t]);
  const volumeTitleText = useMemo(() => t("ui_settings_volume"), [t]);

  // Don't render if texture not loaded or fully closed
  if (!popupFrameTexture || !viewportConfig) {
    return null;
  }
  if (!settingsActive && !isClosing) return null;

  return (
    <Container
      ref={containerRef}
      name="secondary_settings_overlay"
      zIndex={10}
      sortableChildren
      eventMode="static"
    >
      {showPopupFrame ? (
        <Graphics
          name="secondary_settings_click_outside"
          draw={(g) => {
            g.clear();
            g.beginFill(0x000000, 0.001);
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          alpha={overlayAlpha > 0 ? 1 : 0}
          eventMode="static"
          zIndex={0}
          onpointertap={handleClose}
        />
      ) : (
        <Graphics
          name="secondary_settings_background"
          draw={drawBackground}
          alpha={overlayAlpha}
          // Fullscreen mode (mobile portrait/landscape, tablet portrait): the
          // popup may ONLY be closed via the X button — tapping the background
          // must not dismiss it. eventMode stays "static" so the fill still
          // absorbs taps (blocks the game underneath), it just no longer closes.
          eventMode="static"
          zIndex={0}
        />
      )}

      {/* Settings Popup Frame - Centered */}
      <Container
        name="secondary_settings_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
        zIndex={1}
      >
        {/* Background Graphics for Mobile & Tablet Portrait - covers full screen */}
        {!showPopupFrame && (
          <Graphics
            name="secondary_settings_popup_background"
            draw={drawPopupBackground}
          />
        )}

        {/* Popup Frame Sprite - centered, maintains aspect ratio */}
        <Sprite
          texture={popupFrameTexture}
          anchor={0.5}
          width={popupSize.width}
          height={popupSize.height}
          roundPixels={true}
        />

        {/* Content Viewport Container - positioned relative to parent container */}
        <Container
          name="secondary_settings_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
        >

          {/* Settings Title */}
          <Text
            text={settingsTitleText}
            style={sectionStyle}
            anchor={{ x: 0, y: 0 }}
            x={
              showPopupFrame
                ? 0
                : device === PixiSettings.DEVICE.MOBILE && orientation === PixiSettings.ORIENTATION.LANDSCAPE
                  ? -8
                  : 0
            }
            y={0}
            roundPixels={true}
          />

          {/* Close Button (Windowed mode - inside content area) */}
          {showPopupFrame && (
            <Container
              name="secondary_settings_close_button_windowed"
              x={viewportConfig.maxWidth - buttonConfig.width * closeButtonSizeMultiplier * 1.0 / 2}
              y={buttonConfig.height * closeButtonSizeMultiplier * 1.0 / 2}
              zIndex={2}
            >
              <ButtonMenuClose
                onClose={handleClose}
                x={0}
                y={0}
                isSettings={true}
                sizeMultiplier={closeButtonSizeMultiplier * 1.0}
              />
            </Container>
          )}


          {/* Audio Section Title */}
          <Text
            text={audioTitleText}
            style={subtitleStyle}
            anchor={{ x: 0, y: 0 }}
            x={0}
            y={Math.round(itemSpacing * yPositions.audioTitleY)}
            roundPixels={true}
          />

          {/* Volume Slider - full content width */}
          <VolumeBar
            x={showPopupFrame ? viewportConfig.maxWidth * 0.025 : 0}
            y={Math.round(itemSpacing * yPositions.volumeBarY)}
            value={volumeToSlider(masterVolume)}
            onChange={handleVolumeChange}
            buttonConfig={buttonConfig}
            maxWidth={showPopupFrame ? viewportConfig.maxWidth * 0.95 : (orientation === PixiSettings.ORIENTATION.PORTRAIT ? viewportConfig.maxWidth : viewportConfig.maxWidth * 0.55)}
            heightScale={1.0}

          />

          {/* Volume Label - positioned below volume bar */}
          <Text
            text={volumeTitleText}
            style={subtitleStyle}
            anchor={{ x: 0, y: 0 }}
            x={0}
            y={Math.round(itemSpacing * yPositions.volumeTitleY)}
            roundPixels={true}
          />

          {/* Music Checkbox */}
          <Checkbox
            translationKey="ui_settings_music"
            checked={backgroundMusicEnabled}
            onChange={handleMusicToggle}
            x={Math.round(checkboxSize / 2)}
            y={Math.round(itemSpacing * yPositions.musicY)}
            sizeMultiplier={0.7}
            labelFontSize={layoutConfig.checkboxLabelFontSize}
          />

          {/* Sound FX Checkbox */}
          <Checkbox
            translationKey="ui_settings_sound"
            checked={soundEffectsEnabled}
            onChange={handleSoundFXToggle}
            x={Math.round(checkboxSize / 2)}
            y={Math.round(itemSpacing * yPositions.soundFXY)}
            sizeMultiplier={0.7}
            labelFontSize={layoutConfig.checkboxLabelFontSize}
          />
        </Container>
      </Container>

      {/* Close Button - fullscreen only (windowed close is inside content viewport) */}
      {!showPopupFrame && (
        <Container zIndex={2}>
          <ButtonMenuClose
            onClose={handleClose}
            x={closeButtonPosition.x}
            y={closeButtonPosition.y}
            isSettings={true}
            sizeMultiplier={closeButtonSizeMultiplier}
          />
        </Container>
      )}
    </Container>
  );
};

export default Settings;
